import { useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import { BSSelect } from "../reactSelectBSWrapper";
import { FormControlWrapper } from "./formControlWrapper";

// Debounce a promise-returning fn: collapses rapid calls (keystrokes) into one
// invocation, resolving every pending caller with the latest result. Keeps the
// async search from firing a request per keystroke.
export const debouncePromise = (fn, waitMs) => {
	let timer;
	let pending = [];

	return (...args) =>
		new Promise((resolve, reject) => {
			clearTimeout(timer);
			pending.push({ resolve, reject });
			timer = setTimeout(() => {
				const waiters = pending;
				pending = [];
				fn(...args).then(
					(result) => waiters.forEach((w) => w.resolve(result)),
					(error) => waiters.forEach((w) => w.reject(error))
				);
			}, waitMs);
		});
};

// Single-select dropdown backed by a server-side search (react-select async).
// Use instead of DROPDOWN when the option set is too large to load up front
// (e.g. org users). `loadOptions(inputValue)` must resolve to
// [{ id, value, label }] and is expected to be cached by the caller; keystroke
// debouncing is handled here (`debounceMs`).
export const FieldAsyncDropdown = ({
	isDisabled,
	error,
	placeholder = "",
	title = "",
	autoFocus,
	tooltip,
	loadOptions = async () => [],
	defaultOptions = true,
	debounceMs = 500,
	setValue = (_value) => {},
}) => {
	const [selected, setSelected] = useState(null);

	const debouncedLoadOptions = useMemo(
		() => debouncePromise(loadOptions, debounceMs),
		[loadOptions, debounceMs]
	);

	const handleChange = (option) => {
		setSelected(option);
		setValue(option ? option.value : undefined);
	};

	return (
		<Form.Group data-testid="test-fieldAsyncDropdown">
			<FormControlWrapper title={title} tooltip={tooltip} error={error}>
				<BSSelect
					async
					cacheOptions
					defaultOptions={defaultOptions}
					loadOptions={debouncedLoadOptions}
					// The server already filtered; don't let react-select
					// re-filter loaded options against the typed text by label
					// (that would hide matches whose label lacks the term, e.g.
					// a pasted GUID or an email-only match).
					filterOption={null}
					value={selected}
					onChange={handleChange}
					placeholder={placeholder}
					isDisabled={isDisabled}
					isClearable
					error={error}
					autoFocus={autoFocus}
				/>
				{error ? (
					<div
						className="invalid-feedback"
						style={{ display: "contents" }}
					>
						Mandatory field
					</div>
				) : null}
			</FormControlWrapper>
		</Form.Group>
	);
};
